import { useDraggable, useDroppable } from "@dnd-kit/core";
import React, { memo, useCallback } from "react";

import { RegistryItem } from "../types";
import { useUnifiedRegistry } from "../unified-registry-context";

import { useDragDrop } from "./DragDropProvider";
import DroppableFolder from "./DroppableFolder";

import { Entity, EntityType } from "@/queries/folder-entities/types";

interface DraggableItemProps {
  item: RegistryItem;
  children: React.ReactNode;
  className?: string;
  disabled?: boolean;
  droppableDisabled?: boolean;
}

const DraggableItem: React.FC<DraggableItemProps> = memo(
  ({ item, children, className = "", disabled = false, droppableDisabled = false }) => {
    const { activeItem, isDragging, descendantFolderIds } = useDragDrop();

    const registry = useUnifiedRegistry();

    const isSelected =
      registry.isSelected(item as Entity) &&
      activeItem &&
      registry.isSelected(activeItem as Entity);
    const itemId = `${item.type}-${item.id}`;

    const {
      attributes,
      listeners,
      setNodeRef: setDraggableRef,
      isDragging: isThisItemDragging,
    } = useDraggable({
      id: itemId,
      disabled,
      data: {
        type: item.type,
        item,
      },
    });

    const parentFolderId = item.folder_id ?? null;
    const parentAreaDisabled =
      item.type === EntityType.FOLDER ||
      disabled ||
      (parentFolderId !== null && descendantFolderIds.has(parentFolderId));

    const { setNodeRef: setParentAreaRef } = useDroppable({
      id: `parent-area-${item.type}-${item.id}`,
      disabled: parentAreaDisabled,
      data: {
        type: "parent-area",
        parentFolderId,
      },
    });

    const setNodeRef = useCallback(
      (node: HTMLDivElement | null) => {
        setDraggableRef(node);
        setParentAreaRef(node);
      },
      [setDraggableRef, setParentAreaRef],
    );

    const isBeingDragged =
      isSelected || (activeItem?.id === item.id && activeItem?.type === item.type);

    const draggableContent = (
      <div
        ref={setNodeRef}
        className={`
          relative
          ${className}
          ${!isThisItemDragging ? "cursor-grab" : "cursor-grabbing"}
          transition-opacity duration-200 ease-out
        `}
        {...attributes}
        {...listeners}
        data-item-id={item.id}
        data-item-type={item.type}
      >
        <div
          className={`relative h-full ${
            isBeingDragged && isDragging ? "transition-opacity duration-200" : ""
          } ${isBeingDragged && isDragging ? "pointer-events-none opacity-40" : "opacity-100"}`}
        >
          {children}
        </div>
      </div>
    );

    if (item.type === EntityType.FOLDER && !disabled) {
      return (
        <DroppableFolder item={item} disabled={droppableDisabled} className={className}>
          {draggableContent}
        </DroppableFolder>
      );
    }

    return draggableContent;
  },
);

DraggableItem.displayName = "DraggableItem";

export default DraggableItem;
